// 화주 정보 타입 정의
export interface CustomerInfo {
  name: string;
  logoPath: string;
  color: string;
  backgroundColor: string;
  lightColor: string;
}

// 화주 ID 타입
export type CustomerId = 'otoki_ramen' | 'otoki' | 'otoki_pcm' | 'dole' | 'csp' | 'lnb' | 'dr' | 'ys' | 'sy' | 'pac' | 'kdc' | 'dongyangcan' | 'nakto' | 'bukwang' | 'nhlogis';

/** Firestore users.customerId 별칭 → config CustomerId */
const CUSTOMER_ID_ALIASES: Record<string, CustomerId> = {
  pcm: 'otoki_pcm',
};

/** 출고조회만 제공하는 고객 (조달부 등) */
export function isProcurementOnlyCustomer(customerId: CustomerId | null | undefined): boolean {
  return customerId === 'otoki_pcm';
}

// 화주 ID → 데이터베이스 회사 코드 매핑
export const CUSTOMER_COMPANY_MAP: Record<CustomerId, string> = {
  'otoki_ramen': '오뚜기농협물류',
  'otoki': '오뚜기물류서비스(주)',
  'otoki_pcm': '오뚜기조달부',
  'dole': '돌코리아',
  'csp': 'csp',
  'lnb': '(주)엘앤비',
  'dr': '대륭기업(주)',
  'ys': '용신물류',
  'sy': '성양로지스틱스',
  'pac': '푸르미영농조합',
  'kdc': '케이디케미칼',
  'dongyangcan': '동양제관',
  'nakto': '(주)낙토',
  'bukwang': '(주)부광정공',
  'nhlogis': '하나로농협물류'
};

// 화주 정보 매핑 (UI 테마 포함)
export const CUSTOMER_INFO_MAP: Record<CustomerId, CustomerInfo> = {
  'otoki_ramen': {
    name: '오뚜기라면',
    logoPath: '/logos/otoki-logo.png',
    color: '#e53e3e',
    backgroundColor: '#f3f709',
    lightColor: '#faf8e6'
  },
  'otoki': {
    name: '오뚜기물류서비스(주)',
    logoPath: '/logos/otoki-logo.png',
    color: '#e53e3e',
    backgroundColor: '#f3f709',
    lightColor: '#faf8e6'
  },
  'otoki_pcm': {
    name: '오뚜기 조달실',
    logoPath: '/logos/otoki-logo.png',
    color: '#e53e3e',
    backgroundColor: '#f3f709',
    lightColor: '#faf8e6'
  },
  'dole': {
    name: '돌코리아',
    logoPath: '/logos/dole-logo.png',
    color: '#2e7d32',
    backgroundColor: '#fed142',
    lightColor: '#c8e6c9'
  },
  'csp': {
    name: '츄고쿠삼화페인트',
    logoPath: '/logos/csp-logo.png',
    color: '#1976d2',
    backgroundColor: '#dae9ff',
    lightColor: '#bbdefb'
  },
  'lnb': {
    name: '(주)엘앤비',
    logoPath: '/logos/lnb-logo.png',
    color: '#546e7a',
    backgroundColor: '#f5f7fa',
    lightColor: '#eceff1'
  },
  'dr': {
    name: '대륭기업(주)',
    logoPath: '/logos/dr-logo.png',
    color: '#546e7a',
    backgroundColor: '#f5f7fa',
    lightColor: '#eceff1'
  },
  'ys': {
    name: '용신물류',
    logoPath: '/logos/ys-logo.png',
    color: '#546e7a',
    backgroundColor: '#f5f7fa',
    lightColor: '#eceff1'
  },
  'sy': {
    name: '성양로지스틱스',
    logoPath: '/logos/sy-logo.png',
    color: '#546e7a',
    backgroundColor: '#f5f7fa',
    lightColor: '#eceff1'
  },
  'pac': {
    name: '푸르미영농조합',
    logoPath: '/logos/pac-logo.png',
    color: '#546e7a',
    backgroundColor: '#f5f7fa',
    lightColor: '#eceff1'
  },
  'kdc': {
    name: '케이디케미칼',
    logoPath: '/logos/kdc-logo.png',
    color: '#546e7a',
    backgroundColor: '#f5f7fa',
    lightColor: '#eceff1'
  },
  'dongyangcan': {
    name: '동양제관',
    logoPath: '/logos/dongyangcan-logo.png',
    color: '#546e7a',
    backgroundColor: '#f5f7fa',
    lightColor: '#eceff1'
  },
  'nakto': {
    name: '(주)낙토',
    logoPath: '/logos/nakto-logo.png',
    color: '#546e7a',
    backgroundColor: '#f5f7fa',
    lightColor: '#eceff1'
  }
  ,
  'bukwang': {
    name: '(주)부광정공',
    logoPath: '/logos/bugwang-logo.png',
    color: '#546e7a',
    backgroundColor: '#f5f7fa',
    lightColor: '#eceff1'
  },
  'nhlogis': {
    name: '하나로농협물류',
    logoPath: '/logo.png',
    color: '#546e7a',
    backgroundColor: '#f5f7fa',
    lightColor: '#eceff1'
  }
};

// 이메일을 기반으로 화주 식별
export const getCustomerIdFromEmail = (email: string): CustomerId => {
  const e = (email || '').toLowerCase().trim();
  if (e.includes('otoki-pcm@swlogis.kr') || e.includes('otoki-pcm')) {
    return 'otoki_pcm';
  }
  if (e.includes('otoki@swlogis.it.kr')) {
    return 'otoki';
  }
  if (e.includes('ramen@swlogis.it.kr') || e.includes('ottogi')) {
    return 'otoki_ramen';
  }
  if (e.includes('dole@swlogis.it.kr') || e.includes('dole')) {
    return 'dole';
  }
  if (e.includes('csp@swlogis.it.kr') || e.includes('csp')) {
    return 'csp';
  }
  if (e.includes('lnb@swlogis.it.kr') || e.includes('lnb')) {
    return 'lnb';
  }
  if (e.includes('dr@swlogis.it.kr') || e.includes('dr')) {
    return 'dr';
  }
  if (e.includes('ys@swlogis.it.kr') || e.includes('ys')) {
    return 'ys';
  }
  if (e.includes('sy@swlogis.it.kr') || e.includes('sy')) {
    return 'sy';
  }
  if (e.includes('pac@swlogis.it.kr') || e.includes('pac')) {
    return 'pac';
  }
  if (e.includes('kdc@swlogis.it.kr') || e.includes('kdc')) {
    return 'kdc';
  }
  if (e.includes('dongyangcan@swlogis.it.kr') || e.includes('dongyangcan')) {
    return 'dongyangcan';
  }
  if (e.includes('nakto@swlogis.it.kr') || e.includes('nakto')) {
    return 'nakto';
  }
  if (e.includes('bukwang@swlogis.it.kr') || e.includes('bukwang')) {
    return 'bukwang';
  }
  if (e.includes('otoki-nh@swlogis.kr') || e.includes('nhlogis@swlogis.it.kr') || e.includes('nhlogis')) {
    return 'nhlogis';
  }
  return 'otoki_ramen'; // 기본값 (신규 이메일은 users.customerId + resolveCustomerIdFromUserDoc 사용 권장)
};

/**
 * Firestore users 문서의 customerId를 우선합니다.
 * 부서(서브) 계정처럼 이메일 도메인만으로는 구분되지 않는 경우 필수입니다.
 */
export function resolveCustomerIdFromUserDoc(
  email: string,
  customerIdFromFirestore: unknown
): CustomerId {
  const raw = typeof customerIdFromFirestore === 'string' ? customerIdFromFirestore.trim() : '';
  if (raw && CUSTOMER_ID_ALIASES[raw]) {
    return CUSTOMER_ID_ALIASES[raw];
  }
  if (raw && Object.prototype.hasOwnProperty.call(CUSTOMER_INFO_MAP, raw)) {
    return raw as CustomerId;
  }
  return getCustomerIdFromEmail(email);
}

// 품목별 운송료를 표시하지 않을 화주 목록
export const HIDE_ITEM_SALES_COMPANIES = ['오뚜기농협물류', '돌코리아', 'csp', '(주)엘앤비', '대륭기업(주)', '용신물류', '성양로지스틱스', '푸르미영농조합', '케이디케미칼', '동양제관', '하나로농협물류'];

// 새로운 화주 추가 시 체크리스트 (개발용 주석)
/*
새로운 화주 추가 시 수정해야 할 부분:
1. CustomerId 타입에 새 화주 ID 추가
2. CUSTOMER_COMPANY_MAP에 화주ID → 실제회사명 매핑 추가
3. CUSTOMER_INFO_MAP에 화주 정보 (이름, 로고경로, 색상) 추가
4. getCustomerIdFromEmail 함수에 이메일 분기 추가
5. 필요시 HIDE_ITEM_SALES_COMPANIES에 회사명 추가

예시:
- CustomerId에 'new_customer' 추가
- CUSTOMER_COMPANY_MAP['new_customer'] = '새회사명'
- CUSTOMER_INFO_MAP['new_customer'] = { name: '새화주', logoPath: '/logos/new-logo.png', color: '#색상', ... }
- getCustomerIdFromEmail에 새 이메일 조건 추가
*/ 